import { computed, ref, nextTick } from 'vue';
import { listMembers } from '@/js/core/utils/trays-api';
import { VISIBILITY_FORM_MAP } from '@/js/core/constants/tray-constants';
import { useFeaturedMediaUpload } from '@/js/core/composables/useFeaturedMediaUpload';
import {
  membersToTrayAccessState,
  trayAccessPayload,
  trayAccessStateKey,
} from '@/js/core/utils/tray-access';
import { isRichTextEmpty } from '@/js/core/utils/rich-text';
import { scrollToFirstFormError } from '@/js/core/utils/scroll-to-error';

export function useTrayEdit(getTray, emit) {
  const isEditing = ref(false);
  const isSaving = ref(false);

  // Names the field editError refers to, so the form can wire aria-invalid/aria-describedby.
  const errorField = ref('');
  const editError = ref('');

  const fieldErrors = ref({});
  const editFeaturedMediaAltError = ref('');

  const editTitle = ref('');
  const editDescription = ref('');
  const editVisibility = ref('everyone');
  const editCommentAccess = ref('viewers');
  const editVisibleUsers = ref([]);
  const editCommentableUsers = ref([]);
  const originalAccessState = ref('');
  const settingsInitialised = ref(false);

  const snapshotTitle = ref('');
  const snapshotDescription = ref('');

  const {
    imageUrl: editImageUrl,
    focalPoint: editFocalPoint,
    fileName: editFileName,
    alt: editAlt,
    caption: editCaption,
    isUploading,
    uploadError,
    handleFileChange,
    handleFileDrop,
    handleDragOver,
    removeImage: handleRemoveImage,
    cleanup: cleanupFeaturedMedia,
    setMedia,
    updatePayload: mediaUpdatePayload,
  } = useFeaturedMediaUpload();

  const currentAccessPayload = () =>
    trayAccessPayload({
      visibleTo: editVisibility.value,
      commentAccess: editCommentAccess.value,
      visibleUsers: editVisibleUsers.value,
      commentableUsers: editCommentableUsers.value,
    });

  const hasSettingsDirty = computed(
    () =>
      snapshotTitle.value !== editTitle.value ||
      snapshotDescription.value !== editDescription.value ||
      trayAccessStateKey(currentAccessPayload()) !== originalAccessState.value ||
      Object.keys(mediaUpdatePayload()).length > 0
  );

  const handleEdit = async () => {
    const tray = getTray();
    editTitle.value = tray.title || '';
    editDescription.value = tray.content || '';
    editVisibility.value = VISIBILITY_FORM_MAP[tray.visibility] || 'everyone';
    editCommentAccess.value = tray.comment_access || 'viewers';
    editVisibleUsers.value = [];
    editCommentableUsers.value = [];
    editFeaturedMediaAltError.value = '';
    setMedia({
      imageUrl: tray.featured_media_url || null,
      attachmentId: tray.featured_media_id || null,
      focalPoint: tray.focal_point ? { ...tray.focal_point } : null,
      alt: tray.featured_media_alt || '',
      caption: tray.featured_media_caption || '',
    });
    editError.value = '';
    errorField.value = '';
    fieldErrors.value = {};
    isEditing.value = true;

    try {
      const result = await listMembers(tray.uuid);
      const members = result.data || [];
      const accessState = membersToTrayAccessState(members);
      editVisibleUsers.value = accessState.visibleUsers;
      editCommentableUsers.value = accessState.commentableUsers;
    } catch (err) {
      editError.value = err.message || 'Failed to load selected users.';
    }

    originalAccessState.value = trayAccessStateKey(currentAccessPayload());
    snapshotTitle.value = editTitle.value;
    snapshotDescription.value = editDescription.value;
    settingsInitialised.value = true;

    await nextTick();
  };

  const cancelEdit = () => {
    cleanupFeaturedMedia();
    settingsInitialised.value = false;
    isEditing.value = false;
    editError.value = '';
    errorField.value = '';
    fieldErrors.value = {};
    editFeaturedMediaAltError.value = '';
    uploadError.value = '';
  };

  const validateFeaturedMediaAlt = () => {
    editFeaturedMediaAltError.value = '';

    if (editImageUrl.value && !editAlt.value.trim()) {
      editFeaturedMediaAltError.value = 'Alt text is required for featured media';

      return false;
    }

    return true;
  };

  const updateEditAlt = value => {
    editAlt.value = value;

    if (value.trim()) {
      editFeaturedMediaAltError.value = '';
    }
  };

  const removeEditImage = () => {
    handleRemoveImage();
    editFeaturedMediaAltError.value = '';
  };

  const saveEdit = () => {
    const description = editDescription.value;
    const title = editTitle.value.trim();

    fieldErrors.value = {};
    errorField.value = '';

    if (!title) {
      editError.value = 'Title is required.';
      errorField.value = 'title';
      scrollToFirstFormError('.tray-edit-form__section');
      return;
    }

    if (isRichTextEmpty(description)) {
      editError.value = 'Description is required.';
      errorField.value = 'description';
      scrollToFirstFormError('.tray-edit-form__section');
      return;
    }

    if (!validateFeaturedMediaAlt()) {
      scrollToFirstFormError('.tray-edit-form__section');
      return;
    }

    isSaving.value = true;
    editError.value = '';

    const accessPayload = currentAccessPayload();
    const accessChanged = trayAccessStateKey(accessPayload) !== originalAccessState.value;

    const payload = {
      title,
      description,
    };

    if (accessChanged) {
      Object.assign(payload, accessPayload);
    }

    Object.assign(payload, mediaUpdatePayload());

    emit('update', payload);
  };

  const onSaveComplete = () => {
    isSaving.value = false;
    isEditing.value = false;
  };

  const onSaveError = message => {
    isSaving.value = false;
    editError.value = message;
  };

  return {
    isEditing,
    isSaving,
    settingsInitialised,
    hasSettingsDirty,
    editError,
    errorField,
    fieldErrors,
    editTitle,
    editDescription,
    editVisibility,
    editCommentAccess,
    editVisibleUsers,
    editCommentableUsers,
    editImageUrl,
    editFocalPoint,
    editFileName,
    editAlt,
    editCaption,
    editFeaturedMediaAltError,
    isUploading,
    uploadError,
    handleEdit,
    cancelEdit,
    saveEdit,
    onSaveComplete,
    onSaveError,
    handleFileChange,
    handleFileDrop,
    handleDragOver,
    handleRemoveImage: removeEditImage,
    updateEditAlt,
  };
}
